import { type KeyboardEvent, useRef, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import {
  Avatar,
  Box,
  Chip,
  IconButton,
  Stack,
  type SxProps,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Camera, Pencil, User } from 'lucide-react';

import type { GetUserQuery } from '@graphql/__generated__/graphql';
import { Dictionaries_Types_Enum_Enum } from '@graphql/__generated__/graphql';
import { GET_DICTIONARY } from '@modules/_shared/components/dictionary-select/DictionarySelect';
import { AgentImageUploadModal } from '@modules/_shared/components/image-upload/implementations/AgentImageUploadModal';
import { ListingAccordion } from '@modules/_shared/components/ListingAccordion';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { getDictionaryLabel } from '@modules/_shared/utils/dictionaries';
import { useAutoTour } from '@modules/product-updates/tours/TourProvider';
import { tourAnchor } from '@modules/product-updates/tours/tourTypes';
import { AgentProfileCompletion } from '@modules/users/user-overview/components/AgentProfileCompletion';
import { SaveCancelButtons } from '@modules/users/user-overview/components/SaveCancelButtons';
import { UserPermissionsMenu } from '@modules/users/user-overview/components/UserPermissionsMenu';
import { useCanViewSubscriptions } from '@modules/users/user-overview/hooks/useCanViewSubscriptions';
import { PROFILE_PHOTO_ANCHOR } from '@modules/users/user-overview/profilePhotoTour';
import { GET_USER, UPDATE_USER_NAME } from '@modules/users/userQueries';
import { useLocale } from '@shared/hooks/locale';
import { customPalette } from '@shared/theme/StyleProvider';

const roleChipSx: SxProps = {
  height: 20,
  fontSize: 11,
  fontWeight: 600,
  '& .MuiChip-label': { px: 0.875 },
};

const nameInputSx: SxProps = {
  '& .MuiOutlinedInput-root': {
    background: customPalette.white,
    borderRadius: '8px',
  },
  '& .MuiOutlinedInput-input': {
    py: '6px',
    fontSize: '1rem',
    fontWeight: 600,
  },
};

type UserOverviewCardProps = {
  user: NonNullable<GetUserQuery['users_by_pk']>;
};

export const UserOverviewCard = ({ user }: UserOverviewCardProps) => {
  const { t } = useLocale();
  const { me } = useAppData();
  const isAdmin = me?.is_admin ?? false;
  const canViewSubscriptions = useCanViewSubscriptions(user.id);

  const userFirstName = user.first_name ?? '';
  const userLastName = user.last_name ?? '';

  const [apolloError, setApolloError] = useState<ErrorLike | null>(null);
  const [editing, setEditing] = useState(false);
  const [firstName, setFirstName] = useState(userFirstName);
  const [lastName, setLastName] = useState(userLastName);
  const [photoUploaderOpen, setPhotoUploaderOpen] = useState(false);

  const lastSyncedName = useRef({ first: userFirstName, last: userLastName });
  if (
    !editing &&
    (lastSyncedName.current.first !== userFirstName ||
      lastSyncedName.current.last !== userLastName)
  ) {
    lastSyncedName.current = { first: userFirstName, last: userLastName };
    setFirstName(userFirstName);
    setLastName(userLastName);
  }

  const imageUrl = user.user_images?.[0]?.image?.url ?? undefined;
  const fullName = user.full_name ?? '-';
  const isBrokerOrAdmin = user.is_broker || user.is_admin;
  const canEditPhoto = isAdmin || me?.id === user.id;
  useAutoTour('profile-photo', me?.id === user.id);
  const freeCrmRelevant = ['FR', 'ES', 'IT'].includes(
    me?.tenant?.country_code ?? '',
  );

  const [updateUserName, { loading: saving }] = useMutation(UPDATE_USER_NAME, {
    refetchQueries: [
      {
        query: GET_USER,
        variables: { id: user.id, includeSubscriptions: canViewSubscriptions },
      },
    ],
    awaitRefetchQueries: true,
    onError: setApolloError,
  });

  const { data: membershipTypesData } = useQuery(GET_DICTIONARY, {
    variables: { type: Dictionaries_Types_Enum_Enum.MembershipTypes },
    skip: !isAdmin && !user.membership_type,
  });

  const membershipTypes = membershipTypesData?.dictionaries ?? [];

  const membershipTypeLabel = user.membership_type
    ? getDictionaryLabel(user.membership_type, membershipTypes)
    : null;

  const resetNameFields = () => {
    setFirstName(userFirstName);
    setLastName(userLastName);
  };

  const openEdit = () => {
    resetNameFields();
    setEditing(true);
  };

  const cancelEdit = () => {
    resetNameFields();
    setEditing(false);
  };

  const handleSave = async () => {
    const trimmedFirst = firstName.trim();
    const trimmedLast = lastName.trim();
    if (trimmedFirst !== userFirstName || trimmedLast !== userLastName) {
      await updateUserName({
        variables: {
          id: user.id,
          first_name: trimmedFirst || null,
          last_name: trimmedLast || null,
        },
      });
    }
    setEditing(false);
  };

  const handleNameFieldKeyDown = (evt: KeyboardEvent) => {
    if (evt.key === 'Enter') {
      evt.preventDefault();
      void handleSave();
    } else if (evt.key === 'Escape') {
      cancelEdit();
    }
  };

  return (
    <ListingAccordion defaultExpanded disableCollapse standalone>
      <Stack direction="row" sx={{ alignItems: 'flex-start', gap: 1.5 }}>
        <Box
          {...tourAnchor(PROFILE_PHOTO_ANCHOR)}
          sx={{
            position: 'relative',
            width: 56,
            height: 56,
            flexShrink: 0,
            borderRadius: '50%',
            cursor: canEditPhoto ? 'pointer' : 'default',
            '&:hover .avatar-edit-overlay': canEditPhoto
              ? { opacity: 1 }
              : undefined,
          }}
          onClick={canEditPhoto ? () => setPhotoUploaderOpen(true) : undefined}
          role={canEditPhoto ? 'button' : undefined}
          tabIndex={canEditPhoto ? 0 : undefined}
          onKeyDown={
            canEditPhoto
              ? evt => {
                  if (evt.key === 'Enter' || evt.key === ' ') {
                    evt.preventDefault();
                    setPhotoUploaderOpen(true);
                  }
                }
              : undefined
          }
          aria-label={canEditPhoto ? t('Change profile photo') : undefined}
        >
          <Avatar
            src={imageUrl}
            alt={user.first_name ?? ''}
            sx={{ width: 56, height: 56 }}
          >
            {!imageUrl && <User size={34} />}
          </Avatar>
          {canEditPhoto && (
            <Box
              className="avatar-edit-overlay"
              sx={{
                position: 'absolute',
                inset: 0,
                borderRadius: '50%',
                bgcolor: 'rgba(0, 0, 0, 0.5)',
                color: 'common.white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: 0,
                transition: 'opacity 0.15s ease',
                pointerEvents: 'none',
              }}
            >
              <Camera size={20} />
            </Box>
          )}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          {editing ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 0.5,
                minWidth: 0,
              }}
            >
              <Box sx={{ display: 'flex', gap: 0.5, flex: 1, minWidth: 0 }}>
                <TextField
                  value={firstName}
                  onChange={evt => setFirstName(evt.target.value)}
                  placeholder={t('First name')}
                  size="small"
                  variant="outlined"
                  autoFocus
                  disabled={saving}
                  sx={nameInputSx}
                  onKeyDown={handleNameFieldKeyDown}
                />
                <TextField
                  value={lastName}
                  onChange={evt => setLastName(evt.target.value)}
                  placeholder={t('Last name')}
                  size="small"
                  variant="outlined"
                  disabled={saving}
                  sx={nameInputSx}
                  onKeyDown={handleNameFieldKeyDown}
                />
              </Box>
              <SaveCancelButtons
                onSave={handleSave}
                onCancel={cancelEdit}
                disabled={saving}
              />
            </Box>
          ) : (
            <Stack
              direction="row"
              sx={{ alignItems: 'center', gap: 0.5, minWidth: 0 }}
            >
              <Typography
                variant="h6"
                noWrap
                sx={{ fontWeight: 600, lineHeight: 1.25, minWidth: 0 }}
              >
                {fullName}
              </Typography>
              <Tooltip title={t('Edit name')}>
                <IconButton
                  size="small"
                  onClick={openEdit}
                  sx={{
                    width: 24,
                    height: 24,
                    color: 'text.secondary',
                    '&:hover': { backgroundColor: 'action.hover' },
                  }}
                >
                  <Pencil size={14} />
                </IconButton>
              </Tooltip>
            </Stack>
          )}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 0.5,
              mt: 0.5,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 0.5,
                flex: 1,
                minWidth: 0,
              }}
            >
              {user.is_broker && (
                <Chip
                  label={t('Agent')}
                  size="small"
                  color="primary"
                  sx={roleChipSx}
                />
              )}
              {!isBrokerOrAdmin && (
                <Chip
                  label={t('User')}
                  size="small"
                  color="default"
                  sx={roleChipSx}
                />
              )}
              {user.is_deleted && (
                <Chip
                  label={t('Deleted')}
                  size="small"
                  color="error"
                  sx={roleChipSx}
                />
              )}
              {user.is_admin && (
                <Chip
                  label={t('Admin')}
                  size="small"
                  color="error"
                  sx={roleChipSx}
                />
              )}
              {membershipTypeLabel != null && (
                <Chip
                  label={membershipTypeLabel}
                  size="small"
                  variant="outlined"
                  sx={roleChipSx}
                />
              )}
              {user.is_admin && user.can_edit_settings && (
                <Chip
                  label={t('Can edit settings')}
                  size="small"
                  color="warning"
                  sx={roleChipSx}
                />
              )}
              {user.can_user_login_as && (
                <Chip
                  label={t('Can login as')}
                  size="small"
                  color="secondary"
                  sx={roleChipSx}
                />
              )}
              {freeCrmRelevant && user.has_free_crm_access && (
                <Chip
                  label={t('Free CRM access')}
                  size="small"
                  color="info"
                  sx={roleChipSx}
                />
              )}
            </Box>

            {isAdmin && (
              <UserPermissionsMenu
                user={user}
                membershipTypes={membershipTypes}
              />
            )}
          </Box>
          {user.is_broker && <AgentProfileCompletion user={user} />}
        </Box>
      </Stack>

      <MutationErrorSnackbar
        error={apolloError}
        onClose={() => setApolloError(null)}
      />

      {canEditPhoto && photoUploaderOpen && (
        <AgentImageUploadModal
          userId={user.id}
          currentImageUrl={imageUrl}
          open
          onClose={() => setPhotoUploaderOpen(false)}
        />
      )}
    </ListingAccordion>
  );
};
